import { z } from 'zod';

// Helper to handle NaN values from empty number inputs
const optionalNumber = z.preprocess(
  (val) => (val === '' || val === null || val === undefined || Number.isNaN(val) ? undefined : val),
  z.number().min(0, 'validation.positiveNumber').optional()
);

// Targeting schema for campaign targeting options (matches backend AdTargeting)
export const targetingSchema = z.object({
  categoryIds: z.array(z.number()).optional(),
  countryIds: z.array(z.number()).optional(),
  deviceTypes: z.array(z.enum(['mobile', 'web'])).optional(),
  genders: z.array(z.enum(['men', 'women', 'both'])).optional(),
  ageRanges: z.array(z.object({
    min: z.number().min(13).max(99),
    max: z.number().min(13).max(99),
  })).optional(),
  interests: z.array(z.string()).optional(),
  excludeCategoryIds: z.array(z.number()).optional(),
  keywords: z.array(z.string()).optional(),
});

// Campaign item schema
export const campaignItemSchema = z.object({
  productRefId: z.string().optional(),
  bannerUrl: z.string().url('validation.invalidUrl').optional(),
  bannerLink: z.string().url('validation.invalidUrl').optional(),
  message: z.string().max(500).optional(),
  title: z.string().max(100).optional(),
});

// Main campaign schema for creation/edition
export const campaignSchema = z.object({
  name: z
    .string()
    .min(1, 'validation.required')
    .min(3, 'validation.minLength')
    .max(100, 'validation.maxLength'),
  type: z.enum([
    'sponsored_product',
    'homepage_carousel',
    'featured_shop',
    'category_banner',
    'push_all_users',
    'push_shop_subscribers',
    'push_product_likers',
    'push_single_product_likers',
  ]),
  budgetEur: z.number().min(1, 'validation.positiveNumber'),
  budgetUsd: z.number().min(1, 'validation.positiveNumber'),
  budgetKmf: z.number().min(1, 'validation.positiveNumber'),
  dailyBudgetEur: optionalNumber,
  dailyBudgetUsd: optionalNumber,
  dailyBudgetKmf: optionalNumber,
  bidAmountEur: optionalNumber,
  bidAmountUsd: optionalNumber,
  bidAmountKmf: optionalNumber,
  startDate: z
    .string()
    .min(1, 'validation.required'),
  endDate: z
    .string()
    .min(1, 'validation.required'),
  targeting: targetingSchema.nullish(),
  items: z.array(campaignItemSchema).nullish(),
}).refine(
  (data) => {
    if (data.startDate && data.endDate) {
      return new Date(data.startDate) < new Date(data.endDate);
    }
    return true;
  },
  {
    message: 'validation.endDateAfterStartDate',
    path: ['endDate'],
  }
);

// Wallet recharge schema
export const rechargeSchema = z.object({
  amount: z
    .number()
    .min(1, 'validation.positiveNumber')
    .max(10000, 'Maximum 10 000€'),
  paymentMethod: z
    .string()
    .optional(),
});

// Sponsored product quick create schema
export const sponsoredProductSchema = z.object({
  productRefId: z
    .string()
    .min(1, 'validation.required'),
  bidAmountEur: z
    .number()
    .min(0.01, 'validation.positiveNumber'),
  dailyBudgetEur: z
    .number()
    .min(1, 'validation.positiveNumber'),
  startDate: z
    .string()
    .min(1, 'validation.required'),
  endDate: z
    .string()
    .min(1, 'validation.required'),
}).refine(
  (data) => {
    if (data.startDate && data.endDate) {
      return new Date(data.startDate) < new Date(data.endDate);
    }
    return true;
  },
  {
    message: 'validation.endDateAfterStartDate',
    path: ['endDate'],
  }
);

// Carousel booking schema
export const carouselBookingSchema = z.object({
  position: z
    .number()
    .min(1)
    .max(5),
  productRefId: z
    .string()
    .min(1, 'validation.required'),
  startDate: z
    .string()
    .min(1, 'validation.required'),
  endDate: z
    .string()
    .min(1, 'validation.required'),
}).refine(
  (data) => {
    if (data.startDate && data.endDate) {
      return new Date(data.startDate) < new Date(data.endDate);
    }
    return true;
  },
  {
    message: 'validation.endDateAfterStartDate',
    path: ['endDate'],
  }
);

export type TargetingFormData = z.infer<typeof targetingSchema>;
export type CampaignItemFormData = z.infer<typeof campaignItemSchema>;
export type CampaignFormData = z.infer<typeof campaignSchema>;
export type RechargeFormData = z.infer<typeof rechargeSchema>;
export type SponsoredProductFormData = z.infer<typeof sponsoredProductSchema>;
export type CarouselBookingFormData = z.infer<typeof carouselBookingSchema>;
